import { Injectable } from '@angular/core';

declare const document: any;

export interface Scripts {
    name: string;
    src: string;
    async?: boolean;
    defer?: boolean;
}

/**
 * A service that will dynamically load third party scripts as needed by services
 * @class
 *
 * We will track what scripts are already added to the DOM so we don't try to load
 * them again.  If scripts are not added, we will go make a blocking call to load them
 * for whatever component may need them after they are initialized.
 */

@Injectable()
export class ScriptService {

    private scripts: any = {};

    /**
     * Registers a script as loadable.  Will still need to load seperate when it's used but
     * should be added in the constructor of any service/component that requires third party
     * script.
     *
     * @param Script
     *
     * ```
     * this.script.add({ name: 'filepicker',  src: 'https://api.filestackapi.com/filestack.js'});
     * ```
     */
     add(script: Scripts) {
        this.scripts[script.name] = {
            loaded: false,
            src: script.src,
            async: script.async || false,
            defer: script.defer || false
        };
    }

    /**
     * Loads multiple scripts at once and returns a promise once all scripts all loaded
     * @param ...scripts
     *
     * ```
     * this.script.load('omniture', 'ckeditor').then(data => {
     *      console.log('scripts loaded ', data); // do stuff
     *   }).catch(error => console.log(error));
     * ```
     */
    load(...scripts: string[]) {
        let promises: any[] = [];
        scripts.forEach((script) => promises.push(this.loadScript(script)));

        return Promise.all(promises);
    }

    /**
     * Used to resolve individual scripts being requested.  Some scripts may load from cache while others require
     * we fetch them.  This method checks the cache before making a request for a script.
     * @param name: string
     */
    loadScript(name: string) {
        return new Promise((resolve, reject) => {

            if (this.scripts[name].loaded) {

                resolve({
                    script: name,
                    loaded: true,
                    status: 'Already Loaded'
                });

            } else {

                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = this.scripts[name].src;
                if(this.scripts[name].async) {
                    script.setAttribute('async','');
                }

                if (script.readyState) {  // IE
                    script.onreadystatechange = () => {
                        if (script.readyState === 'loaded' || script.readyState === 'complete') {
                            script.onreadystatechange = null;
                            this.scripts[name].loaded = true;

                            resolve({
                                script: name,
                                loaded: true,
                                status: 'Loaded'
                            });
                        }
                    };
                } else {  // Others
                    script.onload = () => {
                        this.scripts[name].loaded = true;
                        resolve({
                            script: name,
                            loaded: true,
                            status: 'Loaded'
                        });
                    };
                }

                script.onerror = (error: any) => resolve({
                    script: name,
                    loaded: false,
                    status: 'Loaded'
                });

                document.getElementsByTagName('head')[0].appendChild(script);
            }
        });
    }
}
